// src/games/Quiz.tsx
import { useRef, useState } from "react";
import OpenAI from "openai";
import { useApiKey } from "../ApiKeyContext";
import "./Quiz.css";
import GameRulesCard from "./GameRulesCard";
import "./GameRulesCard.css";
import quizLogo from "../assets/logo-quiz.png";

type QuizProps = {
  onQuit: () => void;
};

export default function Quiz({ onQuit }: QuizProps) {
  const apiKey = useApiKey();
  const openai = new OpenAI({ apiKey, dangerouslyAllowBrowser: true });
  const assistantId = "asst_uBSSeP9vIWYbztS5gDs3y0Si";
  const threadId = useRef<string | null>(null);

  const [showRules, setShowRules] = useState(true);
  const [step, setStep] = useState(0);
  const [team1, setTeam1] = useState("");
  const [team2, setTeam2] = useState("");
  const [currentTeam, setCurrentTeam] = useState("");
  const [level, setLevel] = useState<number | "">("");
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState("");
  const [userAnswer, setUserAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [round, setRound] = useState(1);
  const [scores, setScores] = useState({ team1: 0, team2: 0 });

  const ask = async (prompt: string): Promise<string> => {
    setLoading(true);
    try {
      if (!threadId.current) {
        const t = await openai.beta.threads.create();
        threadId.current = t.id;
      }
      await openai.beta.threads.messages.create(threadId.current, {
        role: "user",
        content: prompt,
      });
      const run = await openai.beta.threads.runs.createAndPoll(threadId.current, {
        assistant_id: assistantId,
      });
      const msgs = await openai.beta.threads.messages.list(threadId.current);
      const msg = msgs.data.filter((m) => m.run_id === run.id && m.role === "assistant").pop();
      const block = msg?.content?.find((b) => b.type === "text");
      return block && "text" in block ? block.text.value : "Erreur de réponse";
    } finally {
      setLoading(false);
    }
  };

  const handle = async () => {
    let res = "";
    if (step === 0) {
      res = await ask("Commencer la partie");
      setStep(1);
    } else if (step === 1) {
      res = await ask(`Équipe 1 : ${team1}`);
      setStep(2);
    } else if (step === 2) {
      res = await ask(`Équipe 2 : ${team2}`);
      setCurrentTeam(team1);
      setStep(3);
    } else if (step === 3 && typeof level === "number") {
      res = await ask(`${currentTeam} se met ${level}`);
      setQuestion(res);
      setStep(4);
    } else if (step === 4) {
      res = await ask(`Question : "${question}". Réponse : "${userAnswer}"`);
      if (/\bbravo\b/i.test(res)) {
        setScores((s) => ({
          ...s,
          [currentTeam === team1 ? "team1" : "team2"]:
            s[currentTeam === team1 ? "team1" : "team2"] +
            (typeof level === "number" ? level : 0),
        }));
      }
      if (round === 10 && currentTeam === team2) {
        setStep(5);
      } else {
        setCurrentTeam(currentTeam === team1 ? team2 : team1);
        if (currentTeam === team2) setRound((r) => r + 1);
        setStep(3);
        setUserAnswer("");
        setLevel("");
      }
    }
    setResponse(res);
  };

  const handleLevelChange = (value: string) => {
    if (value === "") return setLevel("");
    const parsed = parseInt(value, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= 10) {
      setLevel(parsed);
    }
  };

  const adjustLevel = (delta: number) => {
    if (typeof level !== "number") return setLevel(1);
    const newLevel = level + delta;
    if (newLevel >= 1 && newLevel <= 10) setLevel(newLevel);
  };

  const isLevelValid = typeof level === "number" && level >= 1 && level <= 10;

  const baseStyle: React.CSSProperties = {
    position: "relative",
    background: "var(--background)",
    color: "var(--text-light)",
    borderRadius: "12px",
    padding: "24px",
    minHeight: "100vh",
    width: "90%",
    maxWidth: "600px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    boxSizing: "border-box",
    overflow: "hidden",
  };

  return (
    <div className="container" style={baseStyle}>
      <button
        onClick={onQuit}
        disabled={loading}
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          background: "transparent",
          border: "none",
          fontSize: 20,
          cursor: "pointer",
          color: "white",
        }}
        title="Quitter le jeu"
      >
        ❌
      </button>

      {showRules ? (
        <GameRulesCard
          logo={quizLogo}
          gameName='Quiz “Tu te mets combien ?”'
          rules={[
            "▸2 équipes s'affrontent sur 10 rounds.",
            "▸Chaque round, l’équipe active choisit un niveau (1–10).",
            "▸GPT pose une question. Vous répondez à haute voix.",
            "▸Si GPT répond avec “Bravo”, vous marquez les points du niveau.",
          ]}
          onNext={() => setShowRules(false)}
          loading={loading}
        />
      ) : (
        <>
          <h2>🎮 Quiz “Tu te mets combien ?”</h2>
          {loading && <p className="loading">💬 GPT réfléchit…</p>}
          <p>
            <b style={{ color: "#FF6B6B" }}>{team1}</b>: {scores.team1} pts —{" "}
            <b style={{ color: "#4ECDC4" }}>{team2}</b>: {scores.team2} pts
          </p>
          <p>
            Round {round} / 10 —{" "}
            <b style={{ color: currentTeam === team1 ? "#FF6B6B" : "#4ECDC4" }}>
              {currentTeam}
            </b>
          </p>

          {step === 0 && (
            <button className="fullWidth" onClick={handle} disabled={loading}>
              Commencer la partie
            </button>
          )}

          {step === 1 && (
            <>
              {response && <p>{response}</p>}
              <input
                className="fullWidth"
                placeholder="Nom équipe 1"
                value={team1}
                onChange={(e) => setTeam1(e.target.value)}
              />
              <button
                className="fullWidth"
                onClick={handle}
                disabled={!team1 || loading}
              >
                Valider
              </button>
            </>
          )}

          {step === 2 && (
            <>
              {response && <p>{response}</p>}
              <input
                className="fullWidth"
                placeholder="Nom équipe 2"
                value={team2}
                onChange={(e) => setTeam2(e.target.value)}
              />
              <button
                className="fullWidth"
                onClick={handle}
                disabled={!team2 || loading}
              >
                Valider
              </button>
            </>
          )}

          {step === 3 && (
            <>
              {response && <p>{response}</p>}
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <button
                  style={{ fontSize: "1.5rem", width: 50, height: 50 }}
                  onClick={() => adjustLevel(-1)}
                >
                  −
                </button>
                <input
                  className="fullWidth"
                  type="number"
                  min={1}
                  max={10}
                  value={level}
                  onChange={(e) => handleLevelChange(e.target.value)}
                  style={{ textAlign: "center" }}
                />
                <button
                  style={{ fontSize: "1.5rem", width: 50, height: 50 }}
                  onClick={() => adjustLevel(1)}
                >
                  +
                </button>
              </div>
              <button
                className="fullWidth"
                onClick={handle}
                disabled={!isLevelValid || loading}
              >
                Poser question
              </button>
            </>
          )}

          {step === 4 && (
            <>
              <p style={{ fontWeight: "bold" }}>{question}</p>
              <input
                className="fullWidth"
                placeholder="Ta réponse…"
                value={userAnswer}
                onChange={(e) => setUserAnswer(e.target.value)}
              />
              <button
                className="fullWidth"
                onClick={handle}
                disabled={!userAnswer || loading}
              >
                Valider réponse
              </button>
            </>
          )}

          {step === 5 && (
            <>
              <h3>🎉 Fin de la partie</h3>
              <p>
                {team1}: {scores.team1} pts — {team2}: {scores.team2} pts
              </p>
              <button
                className="fullWidth"
                onClick={() => window.location.reload()}
              >
                Rejouer
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
